import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FilePlus2, History as HistoryIcon, Users, Bell, Search, LogOut, Menu, X, Settings as SettingsIcon, BarChart3, UserX } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const baseNavItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, admin: false },
  { to: "/new-pass", label: "New Late Pass", icon: FilePlus2, admin: false },
  { to: "/attendance", label: "Attendance", icon: UserX, admin: false },
  { to: "/history", label: "History", icon: HistoryIcon, admin: false },
  { to: "/reports", label: "Reports", icon: BarChart3, admin: false },
  { to: "/talmidim", label: "Students", icon: Users, admin: false },
  { to: "/settings", label: "Settings", icon: SettingsIcon, admin: true },
];

export default function Layout({ children, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = baseNavItems.filter((n) => !n.admin || isAdmin);
  const currentUserName = user?.name || "User";

  return (
    <div className="min-h-screen flex">
      <aside
        className={`fixed lg:sticky top-0 h-screen z-40 bg-white border-r border-slate-200 shadow-sm transition-all duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } w-72 flex-shrink-0`}
      >
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <img src="/assets/ohr-hatorah-logo.png" alt="Ohr Hatorah" className="w-14 h-14 object-contain flex-shrink-0" />
              <div>
                <div className="font-serif-heb text-xl font-bold text-slate-900 leading-none" dir="rtl">אור התורה</div>
                <div className="text-[10px] text-slate-500 font-en tracking-widest mt-1">LATE PASS SYSTEM</div>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? "text-blue-600" : "text-slate-400"}`} />
                  <span className="flex-1">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-100">
            <Link to="/new-pass" onClick={() => setMobileOpen(false)}>
              <Button className="w-full gradient-blue-mild text-white">
                <FilePlus2 className="w-4 h-4 mr-2" />Quick New Pass
              </Button>
            </Link>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200">
          <div className="h-16 px-4 lg:px-6 flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search student or pass number..." className="pl-10 h-10 bg-slate-50 border-slate-200" />
            </div>

            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 px-3 py-1.5 rounded-lg hover:bg-slate-50">
                  <div className="w-9 h-9 rounded-full gradient-blue-mild text-white flex items-center justify-center font-semibold text-sm" dir="rtl">
                    {currentUserName[0]}
                  </div>
                  <div className="text-left hidden md:block">
                    <div className="text-sm font-medium text-slate-800" dir="rtl">{currentUserName}</div>
                    <div className="text-[10px] text-slate-500">{isAdmin ? "Admin" : "User"}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel dir="rtl" className="text-right">{currentUserName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { onLogout(); navigate("/login"); }} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
